import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  MessageSquare, Maximize2, Minimize2, X, Send, Smile, Paperclip, Mic, 
  RefreshCw, PenLine, MessagesSquare, Square, Loader2,
  TrendingUp, FileText, Calculator, BookOpen, Sparkles, ChevronDown
} from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useChatStore } from '../../hooks/useChatStore';
import { useChatStreaming } from '../../hooks/useChatStreaming';
import { useAudioChat } from '../../hooks/useAudioChat';
import { CloudinaryService } from '../../services/cloudinary/CloudinaryService';
import { EmojiPicker } from './EmojiPicker';
import { ConversationList } from './ConversationList';
import { ChatMessage } from './ChatMessage';
import { TypingIndicator } from './TypingIndicator';
import { SubscriptionErrorBanner } from './SubscriptionErrorBanner';
import { Switch } from '../ui/Switch';

// ============================================================================
// SUGGESTIONS DE PROMPTS COMPTABILITÉ
// ============================================================================

const PROMPT_SUGGESTIONS = [
  {
    id: 'analyze',
    title: 'Analyse financière',
    description: 'Performance, ratios et tendances',
    prompt: 'Analyse la situation financière de mon entreprise',
    icon: TrendingUp,
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400'
  },
  {
    id: 'entries',
    title: 'Écritures comptables',
    description: 'Créer et valider des écritures',
    prompt: 'Aide-moi à passer une écriture comptable',
    icon: BookOpen,
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400'
  },
  {
    id: 'declarations',
    title: 'Déclarations fiscales',
    description: 'TVA, IS, DSF et obligations',
    prompt: 'Quelles sont mes prochaines échéances fiscales ?',
    icon: FileText,
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400'
  },
  {
    id: 'treasury',
    title: 'Trésorerie',
    description: 'Suivi et prévisions de trésorerie',
    prompt: 'Quel est l\'état de ma trésorerie ce mois-ci ?',
    icon: Calculator,
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400'
  }
];

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

interface ChatContainerProps {
  mode: 'floating' | 'fullscreen';
  onClose: () => void;
  onModeChange: () => void;
  embedded?: boolean;
}

export function ChatContainer({ mode, onClose, onModeChange, embedded = false }: ChatContainerProps) {
  // Auth0 pour récupérer le token
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();

  const {
    conversations,
    activeConversationId,
    isTyping,
    connectionStatus,
    isLoading,
    lastError,
    subscriptionError,
    writeMode,
    isStreaming,
    streamingMessageId,
    streamingContent, // 🆕 Pour l'auto-scroll pendant le streaming
    fetchConversations,
    addMessage,
    updateMessage,
    toggleLike,
    toggleDislike,
    createNewConversation,
    deleteConversation,
    setActiveConversation,
    toggleWriteMode,
    clearError,
    connectStreaming,
    cancelCurrentStream
  } = useChatStore();

  // Hook de streaming
  const {
    isStreamingSupported,
    isConnected: isStreamConnected
  } = useChatStreaming();

  // 🎙️ Hook Audio - UI discrète
  const {
    isRecording: isVoiceRecording,
    isProcessing: isVoiceProcessing,
    recordingDuration,
    isSupported: isAudioSupported,
    startRecording: startVoiceRecording,
    stopAndTranscribe,
    cancelRecording
  } = useAudioChat({
    language: 'fr',
    defaultVoice: 'nova',
    onTranscriptionComplete: (text) => {
      setNewMessage(prev => prev ? `${prev} ${text}` : text);
    },
    onError: (error) => {
      console.error('Erreur audio:', error);
    }
  });

  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ============================================================================
  // AUTO-SCROLL INTELLIGENT (v2.5.0)
  // ============================================================================
  // Comportement:
  // - Auto-scroll pendant le streaming (suit la génération du texte)
  // - Si l'utilisateur scrolle vers le haut: auto-scroll se désactive
  // - Réactivation automatique quand l'utilisateur revient proche du bas (< 100px)
  // - À l'envoi d'un message: auto-scroll forcé et réactivé
  // ============================================================================
  
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const isUserScrollingRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  
  // Seuil de distance du bas pour réactiver l'auto-scroll (en pixels)
  const AUTO_SCROLL_THRESHOLD = 100;

  const activeConversation = conversations.find(c => c.id === activeConversationId);
  const messages = activeConversation?.messages || [];

  // Indicateur de connexion
  const isOnline = connectionStatus === 'connected';
  const isReconnecting = connectionStatus === 'reconnecting';

  // Nom de l'utilisateur pour l'accueil
  const userName = user?.given_name || user?.name?.split(' ')[0] || 'là';

  // ============================================================================
  // GESTION DE L'AUTO-SCROLL INTELLIGENT
  // ============================================================================

  /**
   * Vérifie si l'utilisateur est proche du bas de la liste
   */
  const isNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom < AUTO_SCROLL_THRESHOLD;
  }, []);

  /**
   * Scroll vers le bas de la liste
   */
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  /**
   * Force le scroll vers le bas et réactive l'auto-scroll
   * Utilisé lors de l'envoi d'un message
   */
  const forceScrollToBottom = useCallback(() => {
    setIsAutoScrollEnabled(true);
    setShowScrollToBottom(false);
    // Utiliser requestAnimationFrame pour s'assurer que le DOM est mis à jour
    requestAnimationFrame(() => {
      scrollToBottom('smooth');
    });
  }, [scrollToBottom]);

  /**
   * Gère l'événement de scroll de l'utilisateur
   */
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop } = container;
    const isScrollingUp = scrollTop < lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;

    // Si l'utilisateur scrolle vers le haut, désactiver l'auto-scroll
    if (isScrollingUp && !isUserScrollingRef.current) {
      isUserScrollingRef.current = true;
      setIsAutoScrollEnabled(false);
      setShowScrollToBottom(true);
    }

    // Si l'utilisateur revient proche du bas, réactiver l'auto-scroll
    if (isNearBottom()) {
      isUserScrollingRef.current = false;
      setIsAutoScrollEnabled(true);
      setShowScrollToBottom(false);
    } else {
      setShowScrollToBottom(true);
    }
  }, [isNearBottom]);

  /**
   * Auto-scroll pendant le streaming (si activé)
   * Surveille à la fois l'état isStreaming ET le contenu streamingContent
   * pour déclencher le scroll à chaque nouveau chunk reçu
   */
  useEffect(() => {
    if (isAutoScrollEnabled && (isStreaming || isTyping)) {
      // Utiliser requestAnimationFrame pour un scroll fluide pendant le streaming
      const animationFrame = requestAnimationFrame(() => {
        scrollToBottom('smooth');
      });
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [isAutoScrollEnabled, isStreaming, isTyping, streamingContent, scrollToBottom]);

  /**
   * Scroll vers le bas quand de nouveaux messages arrivent (si auto-scroll activé)
   */
  useEffect(() => {
    if (isAutoScrollEnabled && messages.length > 0) {
      scrollToBottom('smooth');
    }
  }, [messages.length, isAutoScrollEnabled, scrollToBottom]);
  
  useEffect(() => {
    fetchConversations();
    
    const handleOnline = () => {
      fetchConversations();
    };
    
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [fetchConversations]);

  // Connexion automatique au streaming
  useEffect(() => {
    const connectWithToken = async () => {
      if (isStreamingSupported && !isStreamConnected && isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          await connectStreaming(token);
        } catch (error) {
          console.warn('Connexion streaming échouée:', error);
        }
      }
    };
    
    connectWithToken();
  }, [isStreamingSupported, isStreamConnected, isAuthenticated, getAccessTokenSilently, connectStreaming]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    const messageToSend = newMessage;
    setNewMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    // 🆕 Forcer le scroll vers le bas et réactiver l'auto-scroll
    forceScrollToBottom();
    await addMessage(messageToSend, 'user');
  };

  const handleStop = () => {
    cancelCurrentStream();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isStreaming) {
        handleStop();
      } else if (newMessage.trim()) {
        handleSend();
      }
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Upload vers Cloudinary au lieu d'envoyer du base64
      const result = await CloudinaryService.uploadChatAttachment(file);
      
      await addMessage('Pièce jointe envoyée', 'user', {
        name: file.name,
        type: file.type,
        content: result.url  // URL Cloudinary au lieu de base64
      });
    } catch (error) {
      console.error('Erreur upload pièce jointe:', error);
      // Fallback: si Cloudinary échoue, informer l'utilisateur
      await addMessage('❌ Erreur lors de l\'upload de la pièce jointe. Veuillez réessayer.', 'bot');
    }

    // Reset l'input file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRetry = () => {
    clearError();
    fetchConversations();
  };

  // Formater la durée d'enregistrement
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className={`
        bg-card flex flex-col relative
        ${mode === 'floating' 
          ? 'fixed bottom-4 right-4 w-[400px] h-[600px] rounded-lg shadow-xl' 
          : 'w-full h-full'
        }
        ${mode === 'fullscreen' && !embedded ? 'rounded-none' : ''}
        ${embedded ? 'border-0' : 'rounded-lg'}
      `}
      style={{
        maxWidth: mode === 'floating' ? '90vw' : '100vw',
        maxHeight: mode === 'floating' ? '90vh' : '100vh'
      }}
    >
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between px-4 py-3 border-b bg-card">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${lastError ? 'bg-red-500/10' : 'bg-primary/10'}`}>
              <MessageSquare className={`h-4 w-4 ${lastError ? 'text-red-500' : 'text-primary'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-sm">ADHA Assistant</h3>
              {lastError ? (
                <button 
                  onClick={handleRetry}
                  className="text-xs text-red-500 hover:text-red-600 flex items-center space-x-1 group"
                >
                  <span>Hors ligne</span>
                  <RefreshCw className="h-3 w-3 group-hover:animate-spin" />
                </button>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {isLoading ? 'Chargement...' : isReconnecting ? 'Reconnexion...' : isOnline ? 'En ligne' : 'Hors ligne'}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1">
            {lastError && (
              <button
                onClick={clearError}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors text-xs"
                title="Ignorer l'erreur"
              >
                <X className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={onModeChange}
              className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title={mode === 'floating' ? 'Agrandir' : 'Réduire'}
            >
              {mode === 'floating' ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {mode === 'fullscreen' && !embedded && (
          <ConversationList
            conversations={conversations}
            activeId={activeConversationId}
            onSelect={setActiveConversation}
            onDelete={deleteConversation}
            onNew={createNewConversation}
          />
        )}

        <div className="flex-1 flex flex-col overflow-hidden bg-background">
          {/* Zone de messages avec auto-scroll intelligent */}
          <div 
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto chat-scrollbar relative"
          >
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-1">
              {messages.length === 0 ? (
                /* ========== PAGE D'ACCUEIL COMPTABILITÉ ========== */
                <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center px-4">
                  {/* Message de bienvenue */}
                  <h2 className="text-2xl font-semibold text-foreground mb-2">
                    Bonjour, {userName} 👋
                  </h2>
                  <p className="text-muted-foreground mb-8 max-w-md">
                    Je suis <span className="font-medium text-primary">ADHA</span>, votre assistant comptable intelligent. 
                    Comment puis-je vous aider aujourd'hui ?
                  </p>
                  
                  {/* Grille de suggestions */}
                  <div className="grid gap-3 w-full max-w-lg grid-cols-1 md:grid-cols-2">
                    {PROMPT_SUGGESTIONS.map((suggestion) => {
                      const IconComponent = suggestion.icon;
                      return (
                        <button 
                          key={suggestion.id}
                          onClick={() => setNewMessage(suggestion.prompt)}
                          className="group flex items-start gap-3 p-4 bg-card border border-border rounded-xl hover:border-primary/50 hover:bg-primary/5 dark:hover:bg-primary/10 transition-all text-left"
                        >
                          <div className={`w-10 h-10 rounded-lg ${suggestion.bgColor} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                            <IconComponent className={`h-5 w-5 ${suggestion.iconColor}`} />
                          </div>
                          <div>
                            <h4 className="font-medium text-foreground text-sm">
                              {suggestion.title}
                            </h4>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {suggestion.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Tip */}
                  <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
                    <Sparkles className="h-3 w-3" />
                    <span>Tapez votre question ou utilisez une suggestion ci-dessus</span>
                  </div>
                </div>
              ) : (
                /* ========== LISTE DES MESSAGES ========== */
                messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    isStreaming={isStreaming && message.id === streamingMessageId}
                    onEdit={(messageId, content) => updateMessage(messageId, { content })}
                    onLike={toggleLike}
                    onDislike={toggleDislike}
                  />
                ))
              )}
              
              {isTyping && !isStreaming && (
                <TypingIndicator mode="thinking" />
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* 🆕 Bouton "Scroll to bottom" - apparaît quand l'utilisateur a scrollé vers le haut */}
            {showScrollToBottom && messages.length > 0 && (
              <button
                onClick={forceScrollToBottom}
                className="
                  absolute bottom-4 right-4 
                  p-2.5 rounded-full 
                  bg-primary text-white 
                  shadow-lg hover:shadow-xl 
                  transition-all duration-200 
                  hover:scale-105 active:scale-95
                  animate-in fade-in slide-in-from-bottom-2
                  z-10
                "
                title="Aller en bas"
              >
                <ChevronDown className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Sélecteur de mode */}
          <div className="border-t border-border bg-muted/30">
            <div className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center space-x-3">
                <div className={`
                  flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all
                  ${writeMode 
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' 
                    : 'bg-primary/10 text-primary'
                  }
                `}>
                  {writeMode ? (
                    <>
                      <PenLine className="w-3.5 h-3.5" />
                      <span>Mode Écriture</span>
                    </>
                  ) : (
                    <>
                      <MessagesSquare className="w-3.5 h-3.5" />
                      <span>Mode Chat</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className={`text-xs transition-colors ${!writeMode ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                  Chat
                </span>
                <Switch 
                  checked={writeMode} 
                  onCheckedChange={toggleWriteMode}
                  className="data-[state=checked]:bg-orange-500"
                />
                <span className={`text-xs transition-colors ${writeMode ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-muted-foreground'}`}>
                  Écriture
                </span>
              </div>
            </div>
            
            {writeMode && (
              <div className="px-4 pb-2">
                <p className="text-xs text-orange-600/80 dark:text-orange-400/80 flex items-center space-x-1">
                  <span>💡</span>
                  <span>ADHA proposera des écritures comptables</span>
                </p>
              </div>
            )}
          </div>

          {/* Bannière d'erreur d'abonnement/quota */}
          {subscriptionError && (
            <div className="px-3 pb-2">
              <SubscriptionErrorBanner
                error={subscriptionError}
                onDismiss={clearError}
                onRetry={handleRetry}
                compact={mode === 'floating'}
              />
            </div>
          )}

          {/* Zone de saisie */}
          <div className="px-3 pt-2 pb-1.5 border-t bg-card">
            <div className="flex items-end gap-2 relative">
              <textarea
                ref={textareaRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={writeMode ? "Décrivez l'opération comptable..." : "Posez votre question à ADHA..."}
                disabled={isStreaming || isVoiceRecording}
                className={`
                  flex-1 px-3 py-2 pr-12 bg-muted/50 border border-border rounded-lg 
                  focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none 
                  text-sm placeholder:text-muted-foreground/60 transition-all 
                  min-h-[40px] max-h-[120px]
                  ${(isStreaming || isVoiceRecording) ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                rows={1}
                style={{ height: 'auto', overflow: 'hidden' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                }}
              />
              
              {/* ✅ Bouton dynamique Send/Stop/Mic - style WhatsApp/Claude */}
              {isStreaming ? (
                // STOP pendant streaming IA
                <button
                  onClick={handleStop}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all bg-red-500 hover:bg-red-600 text-white shadow-sm hover:shadow-md"
                  title="Arrêter la génération"
                >
                  <Square className="h-4 w-4 fill-current" />
                  <span className="absolute inset-0 rounded-lg bg-red-400 animate-ping opacity-30" />
                </button>
              ) : isVoiceRecording ? (
                // STOP pendant enregistrement vocal
                <button
                  onClick={async () => {
                    try {
                      await stopAndTranscribe();
                    } catch (err) {
                      console.error('Erreur transcription:', err);
                    }
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all bg-red-500 hover:bg-red-600 text-white shadow-sm hover:shadow-md"
                  title="Arrêter et transcrire"
                >
                  <Square className="h-4 w-4 fill-current" />
                  <span className="absolute inset-0 rounded-lg bg-red-400 animate-ping opacity-30" />
                </button>
              ) : isVoiceProcessing ? (
                // Traitement transcription
                <button
                  disabled
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-primary/20 text-primary"
                  title="Transcription en cours..."
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                </button>
              ) : newMessage.trim() ? (
                // SEND quand texte présent
                <button
                  onClick={handleSend}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all duration-200 bg-primary text-white hover:bg-primary/90 shadow-sm hover:shadow-md hover:scale-105"
                  title="Envoyer (Entrée)"
                >
                  <Send className="h-4 w-4" />
                </button>
              ) : isAudioSupported ? (
                // MIC quand input vide
                <button
                  onClick={startVoiceRecording}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all duration-200 bg-primary text-white hover:bg-primary/90 shadow-sm hover:shadow-md hover:scale-105"
                  title="Message vocal"
                >
                  <Mic className="h-4 w-4" />
                </button>
              ) : (
                // Fallback: Send désactivé
                <button
                  disabled
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  title="Tapez un message"
                >
                  <Send className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {/* Actions secondaires et indicateurs */}
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center space-x-2">
                {/* Boutons pièce jointe et emoji */}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isStreaming || isVoiceRecording}
                  className={`
                    p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-colors
                    ${(isStreaming || isVoiceRecording) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted'}
                  `}
                  title="Joindre un fichier"
                >
                  <Paperclip className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  disabled={isStreaming || isVoiceRecording}
                  className={`
                    p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-colors
                    ${(isStreaming || isVoiceRecording) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted'}
                  `}
                  title="Emoji"
                >
                  <Smile className="h-3.5 w-3.5" />
                </button>

                {/* 🎙️ Indicateur d'enregistrement discret */}
                {isVoiceRecording && (
                  <div className="flex items-center space-x-2 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded-lg border border-red-200 dark:border-red-800">
                    {/* Point rouge animé */}
                    <div className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                    </div>
                    {/* Timer */}
                    <span className="text-xs font-mono text-red-600 dark:text-red-400 min-w-[35px]">
                      {formatDuration(recordingDuration)}
                    </span>
                    {/* Bouton annuler */}
                    <button
                      onClick={cancelRecording}
                      className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
                      title="Annuler"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {/* Indicateur de transcription */}
                {isVoiceProcessing && (
                  <div className="flex items-center space-x-2 bg-primary/10 px-2 py-1 rounded-lg">
                    <Loader2 className="h-3 w-3 animate-spin text-primary" />
                    <span className="text-xs text-primary font-medium">Transcription...</span>
                  </div>
                )}
              </div>
              
              <span className="text-[10px] text-muted-foreground">
                {isStreaming ? 'Entrée pour arrêter' : isVoiceRecording ? 'Cliquez pour transcrire' : 'Entrée ↵'}
              </span>
            </div>
            
            {showEmojiPicker && (
              <div className="absolute bottom-16 right-4 z-10 shadow-xl rounded-xl border bg-card">
                <EmojiPicker
                  onSelect={(emoji) => {
                    setNewMessage(prev => prev + emoji);
                    setShowEmojiPicker(false);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
